import { isValidElement } from "react";
import fileDownload from "js-file-download";
import { TableField } from "../../components/common/LynksTable/LynksTable";

/**
 * Efficiently exports all table data to CSV by fetching paginated data in batches
 * @param fetcher - The query fetcher function (e.g., useGetShipmentListQuery.fetcher)
 * @param variables - Current query variables (filter, search, orderBy, etc.)
 * @param dataKey - The key to access data in the response (e.g., "shipments")
 * @param fields - Table field definitions for CSV columns
 * @param filename - Output CSV filename
 * @param onProgress - Optional callback for progress updates
 * @param batchSize - Number of rows to fetch per request (default: 300, max allowed by backend)
 */
export async function exportAllTableData<T, K extends string>({
  fetcher,
  variables,
  dataKey,
  fields,
  filename,
  onProgress,
  batchSize = 300,
}: {
  fetcher: (vars: any) => () => Promise<any>;
  variables: any;
  dataKey: K;
  fields: TableField<T, any>[];
  filename: string;
  onProgress?: (current: number, total?: number) => void;
  batchSize?: number;
}): Promise<void> {
  let allData: T[] = [];
  let skip = 0;
  let totalCount: number | null = null;

  // Fetch all pages
  while (true) {
    // Clean variables - remove null/undefined/empty values to avoid backend issues
    const cleanVariables = Object.entries({
      ...variables,
      take: batchSize,
      skip,
    }).reduce((acc, [key, value]) => {
      // Skip null, undefined
      if (value === null || value === undefined) {
        return acc;
      }
      // Skip empty arrays for orderBy (backend might not expect empty array)
      if (key === "orderBy" && Array.isArray(value) && value.length === 0) {
        return acc;
      }
      // Skip empty strings for search
      if (key === "search" && value === "") {
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {} as any);

    let response;
    try {
      response = await fetcher(cleanVariables)();
    } catch (error) {
      console.error("Export fetch failed with variables:", cleanVariables);
      throw error;
    }

    const result = response[dataKey];
    const batch: T[] = result.data || [];

    // Get total count from first response
    if (totalCount === null && result.count !== null) {
      totalCount = result.count;
    }

    if (!batch.length) break;

    allData = allData.concat(batch);
    skip += batchSize;

    // Report progress
    onProgress?.(allData.length, totalCount || undefined);

    // Stop if we've fetched all data
    if (totalCount !== null && allData.length >= totalCount) break;
    if (batch.length < batchSize) break;
  }

  // Generate CSV
  const csv = await generateCSV(allData, fields);

  // Download
  fileDownload(csv, filename);
}

/**
 * Converts table data to CSV format
 */
async function generateCSV<T>(data: T[], fields: TableField<T, any>[]): Promise<string> {
  // Generate headers
  const headers = fields
    .map((field) => {
      const label =
        typeof field.label === "function" ? field.label() : field.label;
      return escapeCSVValue(label);
    })
    .join(",");

  // Generate rows
  const rows = await Promise.all(
    data.map(async (row) => {
      const cellValues = await Promise.all(
        fields.map(async (field) => {
          let value: any;

          // Prefer an export-only resolver (e.g. async entity-name lookups)
          if (field.exportValue) {
            value = await field.exportValue(row);
          }
          // Then rawValue if available for export
          else if (field.rawValue) {
            value = await field.rawValue(row);
          }
          // Get raw value using sortBy if available for better data quality
          else if (field.sortBy) {
            const sortByFields = Array.isArray(field.sortBy)
              ? field.sortBy
              : [field.sortBy];

            const sortByValue = sortByFields
              .map((sortByField) => getNestedValue(row, sortByField))
              .filter((v) => v != null)
              .join(" ");

            // If sortBy didn't return a meaningful value, fallback to value function
            if (sortByValue && sortByValue.trim() !== "") {
              value = sortByValue;
            } else if (typeof field.value === "function") {
              value = field.value(row);
            } else {
              value = row[field.value];
            }
          } else if (typeof field.value === "function") {
            value = field.value(row);
          } else {
            value = row[field.value];
          }

          return formatCSVValue(value);
        })
      );
      return cellValues.join(",");
    })
  );

  return [headers, ...rows].join("\n");
}

/**
 * Gets a nested value from an object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  const keys = path.split(".");
  let value = obj;

  for (const key of keys) {
    if (value == null) return null;
    value = value[key];
  }

  return value;
}

/**
 * Formats a value for CSV output
 */
function formatCSVValue(value: any): string {
  // Skip React elements - return empty string
  if (isValidElement(value)) {
    return '""';
  }

  // Handle dates - convert to ISO string
  if (value instanceof Date) {
    return `"${value.toISOString()}"`;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return `"${value.filter((v) => v != null).join(", ")}"`;
  }

  // Handle null/undefined
  if (value == null) {
    return '""';
  }

  // Handle boolean
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  // Handle numbers
  if (typeof value === "number") {
    return String(value);
  }

  // Handle objects (convert to JSON)
  if (typeof value === "object") {
    return escapeCSVValue(JSON.stringify(value));
  }

  // Handle strings - escape and quote
  return escapeCSVValue(String(value));
}

/**
 * Escapes and quotes a CSV value
 */
function escapeCSVValue(value: string): string {
  // Escape double quotes by doubling them
  const escaped = value.replace(/"/g, '""');

  // Quote if contains comma, newline, or quote
  if (
    escaped.includes(",") ||
    escaped.includes("\n") ||
    escaped.includes('"')
  ) {
    return `"${escaped}"`;
  }

  return escaped;
}
